import React, { useState, useEffect } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import {
  Button, Checkbox, Table, TableBody, TableRow, TableCell, Typography,
  Tooltip, Container, Grid, Paper, TableContainer
} from '@material-ui/core';
import { InfoOutlined as InfoIcon } from '@material-ui/icons';

import MaterialTable from 'material-table';
import { Line as LineChart } from 'react-chartjs-2';

import { serviceClient, requestHistory } from './services';
import { nextDay, formatDate } from './utils';

const styleSelected = {
  backgroundColor: '#eeffee'
};

const useStyles = makeStyles(theme => ({
  selected: styleSelected,
  title: {
    padding: '10px'
  },
  panel: {
    padding: '10px'
  },
  result: {
    '& td': {
      fontWeight: 'bold'
    }
  }
}));

const TOOLTIPS = {
  VIX_volume: 'VIX is a popular measure of the stock market\'s expectation of volatility.',
  stock_attention: 'Number of Google searches for the selected stock ticker (stock-specific attention measure)',
  attention: 'Combination of the attention measures for "financial market", "stock market", and "interest rates" (market-wide attention measure)',
  financial_market_attention: 'Number of Google searches for "financial market" (market-wide attention measure)',
  stock_market_attention: 'Number of Google searches for "stock market" (market-wide attention measure)',
  interest_rate_attention: 'Number of Google searches for "interest rates" (market-wide attention measure)'
};

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

const ONLY_LAST_PREDICTION_IN_CHART = true;


const RequestHistory = ({ features }) => {
  const classes = useStyles();

  const requests = requestHistory.get();

  const formatFeatures = (feats) => feats.map(f => features[f]).join(', ');
  const formatTimestamp = (time) => new Date(time).toLocaleString();

  return (
    <Paper className={classes.panel}>
      <Typography className={classes.title}>
        <b>Past Predictions</b>
      </Typography>
      <MaterialTable
        columns={[
          {
            title: 'Timestamp', field: 'timestamp', width: 200,
            render: (row) => formatTimestamp(row.timestamp)
          },
          {
            title: 'Ticker Symbol', field: 'ticker', width: 100,
          },
          {
            title: 'Predictor Variables', field: 'features',
            render: (row) => formatFeatures(row.features)
          },
          {
            title: 'Prediction', field: 'result'
          }
        ]}
        data={requests}
        title=""
        options={{
          pageSize: 5,
          pageSizeOptions: [5, 10]
        }}
      />
    </Paper>
  );
};

const HistoryChart = ({ ticker, currentPrediction }) => {
  const classes = useStyles();

  const [history, setHistory] = useState([]);

  useEffect(() => {
  if (!ticker) return;
    (async () => {
      setHistory([]);
      const values = await serviceClient.getVolatilityHistory(ticker);
      setHistory(values);
    })();
  }, [ticker]);

  const labels = history.map(entry => entry.date);
  const values = history.map(entry => entry.vola);

  const unique = arr => arr.filter((el, i, a) => i === a.indexOf(el) ? 1 : 0);

  // add historical predictions to history chart
  const maxEntries = 10;
  const predEntries = requestHistory.get().filter(i => i.ticker === ticker);
  const predLabels = unique(predEntries.map(p => p.timestamp.substring(0, 10))).slice(0, maxEntries);
  const predValuesList = predEntries.map(p => p.result);

  // add only last prediction in chart
  if (ONLY_LAST_PREDICTION_IN_CHART) {
    predLabels.length = 0;
    predValuesList.length = 0;
    if (currentPrediction && labels.length > 0) {
      predLabels.push(formatDate(nextDay(labels[labels.length - 1])));
      predValuesList.push(currentPrediction);
    }
  }

  // construct combined labels list and value maps
  const combinedLabels = unique(labels.concat(predLabels)).sort();
  const valuesMap = labels.reduce((res, l, i) => ({...res, [l]: values[i]}), {});
  const predValuesMap = predLabels.reduce((res, l, i) => ({...res, [l]: predValuesList[i]}), {});

  // construct final value arrays
  const predValues = combinedLabels.map(l => predValuesMap[l]);
  const histValues = combinedLabels.map(l => valuesMap[l]);

  const chartData = {
    labels: combinedLabels,
    datasets: [{
      fill: false,
      lineTension: 0.1,
      label: 'Realized volatility (in %, annualized)',
      data: histValues,
      strokeColor: 'grey',
      fillColor: 'grey',
      borderColor: 'grey',
      backgroundColor: 'black'
    }, {
      fill: false,
      lineTension: 0.1,
      label: 'Prediction',
      data: predValues,
      pointRadius: 5,
      type: 'scatter',
      borderColor: 'red',
      backgroundColor: 'red'
    }]
  }
  const chartOptions = {
    scales: {
      yAxes: [{
        ticks: { beginAtZero: true }
      }]
    }
  };

  return (
    <Paper className={classes.panel}>
      <Typography className={classes.title}>
        <b>Volatility History and Prediction</b>
      </Typography>
      <Typography>
        Realized volatility for the last 20 trading days and predicted values (if present)
      </Typography>
      <LineChart data={chartData} options={chartOptions} height={80} />
    </Paper>
  );
};

const MainContent = () => {

  const classes = useStyles();

  const [selectedTicker, setSelectedTicker] = useState('');
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [result, setResult] = useState();
  const [features, setFeatures] = useState([]);
  const [tickers, setTickers] = useState([]);

  useEffect(() => {
    (async () => {
      const configs = await serviceClient.getConfigs();
      setSelectedFeatures(Object.keys(configs.features));
      setTickers(configs.tickers);
      setFeatures(configs.features);
    })();
  }, [])

  const selectTicker = (s) => {
    setSelectedTicker(s);
    setResult(null);
  };
  const selectFeature = (f) => {
    setSelectedFeatures([
      ...selectedFeatures.filter(t => t !== f),
      ...(selectedFeatures.includes(f) ? [] : [f])]);
    setResult(null);
  };
  const compute = async () => {
    const result = await serviceClient.computeResult(selectedTicker, selectedFeatures);
    const historyEntry = {
      ticker: selectedTicker,
      features: selectedFeatures,
      result: result
    };
    requestHistory.add(historyEntry);
    setResult(result);
  };

  const tickersList = tickers.map(s => ({name: s}));

  return (
    <Container>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <Grid item sm={8} style={{margin: 'auto'}}>
            <Paper className={classes.panel}>
              <Typography className={classes.title}>
               This webpage allows for making volatility predictions using an adaption of the prediction methodology presented in <Link to="/about">Audrino, Sigrist, and Ballinari (2020)</Link>.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        <Grid item md={4}>
          <Paper className={classes.panel}>
            <Typography className={classes.title}>
              Please select a <b>stock ticker</b> from the list below:
            </Typography>
            <TableContainer component={Paper}>
              <MaterialTable
                columns={[
                  {
                    title: 'Name', field: 'name'
                  }
                ]}
                data={tickersList}
                onRowClick={(event, row) => selectTicker(row.name)}
                title=""
                options={{
                  pageSize: 10,
                  pageSizeOptions: [10, 20, 50],
                  rowStyle: (row) => row.name === selectedTicker ? styleSelected : {},
                  cellStyle: (cell) => ({paddingTop: '7px', paddingBottom: '7px'}),
                  headerStyle: {paddingTop: '8px', paddingBottom: '8px'}
                }}
              />
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item md={4}>
          <Paper className={classes.panel}>
            <Typography className={classes.title}>
              Please select a combination of <b>predictor variables</b>:
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  {Object.keys(features).map(
                    f => (
                      <TableRow style={{cursor: 'pointer'}} key={f} onClick={() => selectFeature(f)}
                        className={selectedFeatures.includes(f) ? classes.selected : ''}>
                        <TableCell>
                          <Checkbox checked={selectedFeatures.includes(f)} /> {features[f]}
                          <HtmlTooltip
                            title={<React.Fragment>
                              <Typography color="inherit">{TOOLTIPS[f]}</Typography>
                            </React.Fragment>}>
                            <InfoIcon style={{float: 'right', marginTop: '5px'}}/>
                          </HtmlTooltip>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid item md={4}>
          <Paper className={classes.panel}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Selected Stock Ticker:</TableCell>
                  <TableCell>{selectedTicker || 'n/a'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan="2">
                  <Button variant="contained" color="primary" disabled={!selectedTicker} onClick={compute}>
                      Compute Result</Button>
                  </TableCell>
                </TableRow>
                {!!result &&
                  <TableRow className={classes.result}>
                    <TableCell>Result (in %, annualized):</TableCell>
                    <TableCell>{result}</TableCell>
                  </TableRow>
                }
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item md={12}>
          <HistoryChart ticker={selectedTicker} currentPrediction={result} />
        </Grid>
        <Grid item md={12}>
          <RequestHistory features={features} />
        </Grid>
      </Grid>
    </Container>
  );
};

export default MainContent;
